import { MessageCircle, Phone, Video, Sparkles, Shield, Clock } from "lucide-react";

const features = [
    {
        icon: MessageCircle,
        title: "Natural Conversations",
        description: "Chat naturally with AI that truly understands context, personality, and nuance.",
        gradient: "from-primary/20 to-primary/5",
        iconColor: "text-primary",
    },
    {
        icon: Phone,
        title: "Voice Calls",
        description: "Experience real-time voice conversations that feel authentic and personal.",
        gradient: "from-accent-foreground/20 to-accent-foreground/5",
        iconColor: "text-accent-foreground",
    },
    {
        icon: Video,
        title: "Video Chat",
        description: "Face-to-face interactions with lifelike digital twins of your favorite creators.",
        gradient: "from-[hsl(200,80%,50%)]/20 to-[hsl(200,80%,50%)]/5",
        iconColor: "text-[hsl(200,80%,50%)]",
    },
    {
        icon: Sparkles,
        title: "Authentic Personality",
        description: "Each AI twin is carefully crafted to reflect the creator's unique voice and style.",
        gradient: "from-[hsl(280,80%,60%)]/20 to-[hsl(280,80%,60%)]/5",
        iconColor: "text-[hsl(280,80%,60%)]",
    },
    {
        icon: Shield,
        title: "Private & Secure",
        description: "Your conversations are encrypted and never shared. Complete privacy guaranteed.",
        gradient: "from-[hsl(160,70%,45%)]/20 to-[hsl(160,70%,45%)]/5",
        iconColor: "text-[hsl(160,70%,45%)]",
    },
    {
        icon: Clock,
        title: "Always Available",
        description: "Connect anytime, day or night. No scheduling, no waiting, instant access.",
        gradient: "from-[hsl(40,90%,55%)]/20 to-[hsl(40,90%,55%)]/5",
        iconColor: "text-[hsl(40,90%,55%)]",
    },
];

const Features = () => {
    return (
        <section id="features" className="py-32 relative overflow-hidden">
            {/* Background */}
            <div className="absolute inset-0 bg-gradient-to-b from-background via-background/95 to-background" />

            {/* Subtle gradient orbs */}
            <div className="absolute top-1/4 -left-40 w-80 h-80 rounded-full bg-primary/5 blur-[100px]" />
            <div className="absolute bottom-1/4 -right-40 w-80 h-80 rounded-full bg-accent-foreground/5 blur-[100px]" />

            <div className="container relative z-10 px-6">
                <div className="text-center max-w-3xl mx-auto mb-20">
                    <p className="text-primary font-medium mb-4 tracking-wide uppercase text-sm">Features</p>
                    <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 tracking-tight">
                        A New Way to{" "}
                        <span className="gradient-text-vibrant">Connect</span>
                    </h2>
                    <p className="text-foreground/60 text-lg md:text-xl leading-relaxed">
                        Experience the future of creator-fan relationships with cutting-edge AI technology.
                    </p>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
                    {features.map((feature, index) => (
                        <div
                            key={feature.title}
                            className="group relative p-8 rounded-3xl bg-card/40 border border-border/30 hover:border-primary/30 hover:bg-card/60 transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl hover:shadow-primary/10"
                            style={{
                                animationDelay: `${index * 0.1}s`,
                            }}
                        >
                            {/* Icon */}
                            <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-500`}>
                                <feature.icon className={`w-8 h-8 ${feature.iconColor}`} />
                            </div>

                            {/* Content */}
                            <h3 className="text-xl font-semibold mb-3 text-foreground tracking-tight">{feature.title}</h3>
                            <p className="text-foreground/60 leading-relaxed">{feature.description}</p>

                            {/* Hover gradient overlay */}
                            <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-primary/5 via-transparent to-accent-foreground/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default Features;
